import type { Tone } from './primitives';

/**
 * The gallery's Meter and Stat (docs/frontend/handoff, Component Gallery).
 *
 * A Meter is three segments and never a percentage — autonomy is a level,
 * not a proportion. The set-able variant is a proposal, not an instant
 * change: choosing a stage tells the flow what to ask for, and the platform
 * decides. A Stat is a number somebody acts on; the contract forbids one
 * nobody does.
 */
export const LEVELS = ['open_loop', 'mixed', 'closed_loop'] as const;
export type Level = (typeof LEVELS)[number];

export const LEVEL_LABEL: Record<Level, string> = {
  open_loop: 'Open loop — a person approves every change',
  mixed: 'Mixed loop — low-risk changes ship unattended',
  closed_loop: 'Closed loop — the verifier is trusted, the gate closes itself',
};

export const LEVEL_SHORT: Record<Level, string> = {
  open_loop: 'Open loop',
  mixed: 'Mixed',
  closed_loop: 'Closed loop',
};

export function Meter({
  level,
  onSet,
  label = 'How much it decides for itself',
  descriptions = LEVEL_LABEL,
}: {
  level: Level;
  /** Present on the set-able variant. Choosing a stage is a proposal. */
  onSet?: ((level: Level) => void) | undefined;
  label?: string | undefined;
  /** What each level means here, where it is not a department's changes (a project's voice, ADR-0059). */
  descriptions?: Record<Level, string> | undefined;
}) {
  const filled = LEVELS.indexOf(level);
  return (
    <div className="flex flex-col gap-2" data-testid="meter">
      <span className="text-label uppercase tracking-label text-meta">{label}</span>
      <div className={`grid grid-cols-3 gap-[3px] ${onSet ? 'h-[44px]' : 'h-[12px]'}`} role={onSet ? 'radiogroup' : undefined} aria-label={label}>
        {LEVELS.map((l, i) =>
          onSet ? (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={l === level}
              aria-label={LEVEL_SHORT[l]}
              onClick={() => onSet(l)}
              className={`${i <= filled ? 'bg-accent' : 'bg-sunken'} hover:opacity-80`}
            />
          ) : (
            <span key={l} className={`${i <= filled ? 'bg-accent' : 'bg-line'}`} aria-hidden />
          ),
        )}
      </div>
      <div className="grid grid-cols-3 text-micro uppercase tracking-label text-meta">
        {LEVELS.map((l) => (
          <span key={l} className={l === level ? 'text-ink' : ''}>
            {LEVEL_SHORT[l]}
          </span>
        ))}
      </div>
      <p className="m-0 text-dense text-secondary">{descriptions[level]}</p>
    </div>
  );
}

export function Stat({
  value,
  label,
  trend,
  tone = 'neutral',
}: {
  value: string;
  label: string;
  trend?: string | undefined;
  tone?: Tone | undefined;
}) {
  const trendClass = tone === 'accent' ? 'text-accent' : tone === 'warning' ? 'text-warning' : tone === 'danger' ? 'text-danger' : 'text-meta';
  return (
    <div className="flex flex-col gap-1 rounded-brand border border-line bg-raised p-4" data-testid="stat">
      <span className="text-screen font-semibold leading-[1.1]">{value}</span>
      <span className="text-micro uppercase tracking-[0.14em] text-meta">{label}</span>
      {trend && <span className={`text-dense ${trendClass}`}>{trend}</span>}
    </div>
  );
}

/** Dollars, as a person reads them. The cost ledger is USD. */
export const usd = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `$${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
